import { useState, useEffect, useRef, useCallback, FormEvent } from "react";
import {
  Key,
  CheckCircle2,
  Eye,
  EyeOff,
  Save,
  Link,
  Link2,
  Unlink,
  Loader2,
  X,
  RefreshCw,
  ExternalLink,
  BarChart3,
  ChevronRight,
  Search,
  Cloud,
  Cpu,
  Image,
  ShoppingCart,
  Database,
  Plus,
  Pencil,
  Trash2,
  Users,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useTheme } from "../contexts/ThemeContext";
import {
  settings as settingsApi,
  analytics,
  users as usersApi,
  type TokenStatus,
  type CliCheck,
  type ClaudeLoginStatus,
  type GeminiLoginStatus,
  type CodexStatus,
  type CodexLoginStatus,
  type GAConnectionStatus,
  type GAAccount,
  type TenantUser,
  type TenantUserRole,
} from "../lib/api";


// ─── Credential Card ────────────────────────────────────

interface CredentialField {
  key: string;
  label: string;
  placeholder: string;
  type?: "text" | "password";
}

interface CredentialCardConfig {
  provider: string;
  title: string;
  description: string;
  icon: React.ElementType;
  iconColor: string;
  fields: CredentialField[];
  helpUrl?: string;
  helpText?: string;
}

const CREDENTIAL_CARDS: CredentialCardConfig[] = [
  {
    provider: "googleAnalytics",
    title: "Google Analytics",
    description: "OAuth credentials for GA4 data access and property management",
    icon: BarChart3,
    iconColor: "bg-blue-500/10 text-blue-400",
    fields: [
      { key: "clientId", label: "Client ID", placeholder: "xxxxx.apps.googleusercontent.com" },
      { key: "clientSecret", label: "Client Secret", placeholder: "GOCSPX-...", type: "password" },
    ],
    helpUrl: "https://console.cloud.google.com/apis/credentials",
    helpText: "Create OAuth 2.0 credentials in Google Cloud Console",
  },
  {
    provider: "perplexity",
    title: "Perplexity AI",
    description: "API key for Perplexity chat and search-grounded generation",
    icon: Search,
    iconColor: "bg-sky-500/10 text-sky-400",
    fields: [
      { key: "apiKey", label: "API Key", placeholder: "pplx-...", type: "password" },
    ],
    helpUrl: "https://www.perplexity.ai/settings/api",
    helpText: "Get your API key from Perplexity settings",
  },
  {
    provider: "kie",
    title: "KIE.ai (Nanobanana)",
    description: "API key for AI image generation",
    icon: Image,
    iconColor: "bg-pink-500/10 text-pink-400",
    fields: [
      { key: "apiKey", label: "API Key", placeholder: "kie_...", type: "password" },
    ],
  },
  {
    provider: "dataForSeo",
    title: "DataForSEO",
    description: "Credentials for keyword research and SERP analysis",
    icon: Search,
    iconColor: "bg-green-500/10 text-green-400",
    fields: [
      { key: "login", label: "Login", placeholder: "your-email@example.com" },
      { key: "password", label: "Password", placeholder: "API password", type: "password" },
    ],
    helpUrl: "https://app.dataforseo.com/api-dashboard",
    helpText: "Find credentials in DataForSEO API Dashboard",
  },
  {
    provider: "cloudflare",
    title: "Cloudflare",
    description: "API token and account ID for DNS and Cloudflare Pages",
    icon: Cloud,
    iconColor: "bg-amber-500/10 text-amber-400",
    fields: [
      { key: "apiToken", label: "API Token", placeholder: "Bearer token...", type: "password" },
      { key: "accountId", label: "Account ID", placeholder: "32-char hex ID" },
    ],
    helpUrl: "https://dash.cloudflare.com/profile/api-tokens",
    helpText: "Create an API token in Cloudflare dashboard",
  },
  {
    provider: "firecrawl",
    title: "Firecrawl",
    description: "API key for website scraping and crawling",
    icon: Database,
    iconColor: "bg-violet-500/10 text-violet-400",
    fields: [
      { key: "apiKey", label: "API Key", placeholder: "fc-...", type: "password" },
    ],
    helpUrl: "https://firecrawl.dev",
    helpText: "Get your API key from firecrawl.dev",
  },
];

function CredentialCard({
  config,
  savedData,
  onSave,
  onDelete,
}: {
  config: CredentialCardConfig;
  savedData: Record<string, string | boolean> | undefined;
  onSave: (provider: string, values: Record<string, string>) => Promise<void>;
  onDelete: (provider: string) => Promise<void>;
}) {
  const Icon = config.icon;
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showSecrets, setShowSecrets] = useState(false);
  const [values, setValues] = useState<Record<string, string>>({});

  const isConfigured = config.fields.every(
    (f) => savedData?.[`${f.key}_set`] === true
  );

  function startEdit() {
    const v: Record<string, string> = {};
    for (const f of config.fields) {
      v[f.key] = "";
    }
    setValues(v);
    setEditing(true);
    setShowSecrets(false);
  }

  async function handleSave() {
    // Validate all required fields have values
    for (const f of config.fields) {
      if (!values[f.key]?.trim()) {
        alert(`${f.label} is required`);
        return;
      }
    }
    setSaving(true);
    try {
      await onSave(config.provider, values);
      setEditing(false);
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!confirm(`Remove ${config.title} credentials?`)) return;
    try {
      await onDelete(config.provider);
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : "Failed to remove");
    }
  }

  return (
    <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
      <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-800">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${config.iconColor}`}>
          <Icon className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-gray-100">{config.title}</p>
          <p className="text-xs text-gray-500 truncate">{config.description}</p>
        </div>
        {isConfigured && (
          <span className="flex items-center gap-1 text-xs text-green-400 bg-green-500/10 px-2 py-0.5 rounded-full flex-shrink-0">
            <CheckCircle2 className="w-3 h-3" /> Set
          </span>
        )}
      </div>

      <div className="p-4">
        {editing ? (
          <div className="space-y-3">
            {config.fields.map((field) => (
              <div key={field.key}>
                <label className="block text-xs font-medium text-gray-400 mb-1">
                  {field.label}
                </label>
                <div className="relative">
                  <input
                    type={field.type === "password" && !showSecrets ? "password" : "text"}
                    value={values[field.key] ?? ""}
                    onChange={(e) =>
                      setValues((v) => ({ ...v, [field.key]: e.target.value }))
                    }
                    placeholder={field.placeholder}
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm font-mono text-gray-100 placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-brand-500"
                  />
                </div>
              </div>
            ))}

            {config.fields.some((f) => f.type === "password") && (
              <button
                type="button"
                onClick={() => setShowSecrets((v) => !v)}
                className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-300"
              >
                {showSecrets ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                {showSecrets ? "Hide" : "Show"} secrets
              </button>
            )}

            <div className="flex gap-2 pt-1">
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-brand-600 hover:bg-brand-700 disabled:opacity-50 text-white rounded-lg transition-colors"
              >
                <Save className="w-3 h-3" />
                {saving ? "Saving..." : "Save"}
              </button>
              <button
                onClick={() => setEditing(false)}
                className="px-3 py-1.5 text-xs text-gray-400 hover:text-gray-200 bg-gray-800 rounded-lg transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            {isConfigured ? (
              <>
                {config.fields.map((field) => (
                  <div key={field.key} className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">{field.label}</span>
                    <span className="text-xs font-mono text-gray-400">
                      {String(savedData?.[field.key] ?? "****")}
                    </span>
                  </div>
                ))}
                <div className="flex gap-2 pt-2">
                  <button
                    onClick={startEdit}
                    className="flex items-center gap-1 px-2.5 py-1 text-xs bg-gray-800 hover:bg-gray-700 border border-gray-700 text-gray-300 rounded-lg transition-colors"
                  >
                    <Key className="w-3 h-3" /> Update
                  </button>
                  <button
                    onClick={handleDelete}
                    className="flex items-center gap-1 px-2.5 py-1 text-xs text-gray-500 hover:text-red-400 hover:bg-gray-800 border border-gray-700 rounded-lg transition-colors"
                  >
                    <X className="w-3 h-3" /> Remove
                  </button>
                </div>
              </>
            ) : (
              <div className="text-center py-2">
                <p className="text-xs text-gray-500 mb-3">Not configured</p>
                <button
                  onClick={startEdit}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs bg-brand-600 hover:bg-brand-700 text-white rounded-lg transition-colors"
                >
                  <Key className="w-3 h-3" /> Add Credentials
                </button>
                {config.helpUrl && (
                  <a
                    href={config.helpUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block mt-2 text-xs text-gray-600 hover:text-gray-400 transition-colors"
                  >
                    {config.helpText ?? "Get credentials"} <ExternalLink className="w-3 h-3 inline" />
                  </a>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Claude Connection Card ──────────────────────────────

function ClaudeConnectionCard() {
  const [cliStatus, setCliStatus] = useState<CliCheck | null>(null);
  const [tokenStatus, setTokenStatus] = useState<TokenStatus | null>(null);
  const [loginStatus, setLoginStatus] = useState<ClaudeLoginStatus | null>(null);
  const [connecting, setConnecting] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    settingsApi.checkCli().then(setCliStatus).catch(() => setCliStatus({ installed: false }));
    settingsApi.tokenStatus().then(setTokenStatus).catch(() => {});
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  function startPolling() {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const status = await settingsApi.claudeLoginStatus();
        setLoginStatus(status);
        if (status.status === "success" || status.status === "error") {
          if (pollRef.current) clearInterval(pollRef.current);
          pollRef.current = null;
          setConnecting(false);
          if (status.status === "success") {
            settingsApi.tokenStatus().then(setTokenStatus).catch(() => {});
          }
        }
      } catch {}
    }, 2000);
  }

  async function handleConnect() {
    setConnecting(true);
    setLoginStatus(null);
    try {
      const res = await settingsApi.claudeLogin();
      setLoginStatus(res);
      startPolling();
    } catch {
      setConnecting(false);
      setLoginStatus({ status: "error", error: "Failed to start login" });
    }
  }

  async function handleCancel() {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = null;
    setConnecting(false);
    setLoginStatus(null);
    await settingsApi.claudeLoginCancel().catch(() => {});
  }

  async function handleRefresh() {
    setRefreshing(true);
    try {
      const res = await settingsApi.refreshToken();
      setTokenStatus({ hasCredentials: true, valid: true, expiresAt: res.expiresAt });
    } catch {} finally {
      setRefreshing(false);
    }
  }

  function fmtExpiry(ms: number): string {
    const remaining = ms - Date.now();
    if (remaining <= 0) return "Expired";
    const hours = Math.floor(remaining / 3_600_000);
    const days = Math.floor(hours / 24);
    if (days > 0) return `${days}d ${hours % 24}h`;
    const mins = Math.floor((remaining % 3_600_000) / 60_000);
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  }

  const isConnected = tokenStatus?.hasCredentials && tokenStatus?.valid;

  return (
    <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
      <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-800">
        <div className="w-8 h-8 bg-orange-500/10 rounded-lg flex items-center justify-center">
          <Link className="w-4 h-4 text-orange-400" />
        </div>
        <div className="flex-1">
          <p className="text-sm font-semibold text-gray-100">Claude Code CLI</p>
          <p className="text-xs text-gray-500">OAuth connection for AI content generation</p>
        </div>
        {isConnected && (
          <span className="flex items-center gap-1 text-xs text-green-400 bg-green-500/10 px-2 py-0.5 rounded-full">
            <CheckCircle2 className="w-3 h-3" /> Connected
          </span>
        )}
      </div>

      <div className="p-4 space-y-3">
        {cliStatus === null ? (
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <Loader2 className="w-3 h-3 animate-spin" /> Checking CLI...
          </div>
        ) : !cliStatus.installed ? (
          <div className="text-xs text-gray-500">Claude Code CLI not found on the server.</div>
        ) : null}

        {isConnected && tokenStatus.expiresAt && (
          <div className="flex items-center justify-between">
            <div className="text-xs text-gray-400">
              Valid for <span className="text-gray-200 font-medium">{fmtExpiry(tokenStatus.expiresAt)}</span>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={handleRefresh} disabled={refreshing}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-gray-800 hover:bg-gray-700 border border-gray-700 text-gray-300 rounded-lg transition-colors disabled:opacity-50">
                <RefreshCw className={`w-3 h-3 ${refreshing ? "animate-spin" : ""}`} />
                {refreshing ? "Refreshing..." : "Refresh"}
              </button>
              <button onClick={async () => {
                if (!confirm("Disconnect Claude CLI?")) return;
                await settingsApi.claudeDisconnect();
                setTokenStatus({ hasCredentials: false, valid: false, expiresAt: null });
              }}
                className="flex items-center gap-1 px-2.5 py-1.5 text-xs text-gray-500 hover:text-red-400 hover:bg-gray-800 border border-gray-700 rounded-lg transition-colors">
                <Unlink className="w-3 h-3" />
              </button>
            </div>
          </div>
        )}

        {tokenStatus?.hasCredentials && !tokenStatus.valid && (
          <div className="flex items-center justify-between">
            <div className="text-xs text-yellow-400">Token expired.</div>
            <button onClick={handleRefresh} disabled={refreshing}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-brand-600 hover:bg-brand-700 text-white rounded-lg transition-colors disabled:opacity-50">
              <RefreshCw className={`w-3 h-3 ${refreshing ? "animate-spin" : ""}`} />
              {refreshing ? "Refreshing..." : "Refresh"}
            </button>
          </div>
        )}

        {connecting && loginStatus?.status === "waiting_for_auth" && (
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-xs text-blue-400">
              <Loader2 className="w-3 h-3 animate-spin" />
              {loginStatus.message || "Authenticating..."}
            </div>
            {loginStatus.authUrl && (
              <a href={loginStatus.authUrl} target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-2 px-3 py-2 bg-brand-600/10 border border-brand-500/30 rounded-lg text-xs text-brand-400 hover:bg-brand-600/20 transition-colors">
                <ExternalLink className="w-3 h-3" /> Open Authorization Page
              </a>
            )}
            {loginStatus.authCode && (
              <div className="px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-center">
                <p className="text-xs text-gray-500 mb-0.5">Code:</p>
                <p className="text-lg font-mono font-bold text-gray-100 tracking-widest select-all">{loginStatus.authCode}</p>
              </div>
            )}
            <button onClick={handleCancel}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-gray-800 hover:bg-gray-700 border border-gray-700 text-gray-400 rounded-lg transition-colors">
              <X className="w-3 h-3" /> Cancel
            </button>
          </div>
        )}

        {loginStatus?.status === "error" && !connecting && (
          <div className="text-xs text-red-400">{loginStatus.error || "Login failed"}</div>
        )}

        {!isConnected && !connecting && loginStatus?.status !== "error" && cliStatus?.installed && (
          <button onClick={handleConnect}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-brand-600 hover:bg-brand-700 text-white text-xs font-medium rounded-lg transition-colors">
            <Link className="w-3 h-3" /> Connect Claude
          </button>
        )}
      </div>
    </div>
  );
}

function CodexConnectionCard() {
  const [cliStatus, setCliStatus] = useState<CliCheck | null>(null);
  const [codexStatus, setCodexStatus] = useState<CodexStatus | null>(null);
  const [loginStatus, setLoginStatus] = useState<CodexLoginStatus | null>(null);
  const [connecting, setConnecting] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    settingsApi.codexCheck().then(setCliStatus).catch(() => setCliStatus({ installed: false }));
    settingsApi.codexStatus().then(setCodexStatus).catch(() => {});
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  function startPolling() {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const status = await settingsApi.codexLoginStatus();
        setLoginStatus(status);
        if (status.status === "success" || status.status === "error") {
          if (pollRef.current) clearInterval(pollRef.current);
          pollRef.current = null;
          setConnecting(false);
          if (status.status === "success") {
            settingsApi.codexStatus().then(setCodexStatus).catch(() => {});
          }
        }
      } catch {}
    }, 2000);
  }

  async function handleConnect() {
    setConnecting(true);
    setLoginStatus(null);
    try {
      const res = await settingsApi.codexLogin();
      setLoginStatus(res);
      startPolling();
    } catch {
      setConnecting(false);
      setLoginStatus({ status: "error", error: "Failed to start login" });
    }
  }

  async function handleCancel() {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = null;
    setConnecting(false);
    setLoginStatus(null);
    await settingsApi.codexLoginCancel().catch(() => {});
  }

  function fmtExpiry(ms: number): string {
    const remaining = ms - Date.now();
    if (remaining <= 0) return "Expired";
    const hours = Math.floor(remaining / 3_600_000);
    const days = Math.floor(hours / 24);
    if (days > 0) return `${days}d ${hours % 24}h`;
    const mins = Math.floor((remaining % 3_600_000) / 60_000);
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  }

  const isConnected = codexStatus?.hasCredentials && codexStatus?.valid;

  return (
    <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
      <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-800">
        <div className="w-8 h-8 bg-emerald-500/10 rounded-lg flex items-center justify-center">
          <Link2 className="w-4 h-4 text-emerald-400" />
        </div>
        <div className="flex-1">
          <p className="text-sm font-semibold text-gray-100">OpenAI Codex CLI</p>
          <p className="text-xs text-gray-500">OAuth connection for Codex CLI sessions</p>
        </div>
        {isConnected && (
          <span className="flex items-center gap-1 text-xs text-green-400 bg-green-500/10 px-2 py-0.5 rounded-full">
            <CheckCircle2 className="w-3 h-3" /> Connected
          </span>
        )}
      </div>

      <div className="p-4 space-y-3">
        {cliStatus === null ? (
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <Loader2 className="w-3 h-3 animate-spin" /> Checking CLI...
          </div>
        ) : !cliStatus.installed && !codexStatus?.hasCredentials ? (
          <div className="text-xs text-gray-500">Codex CLI not found on the server.</div>
        ) : null}

        {isConnected && codexStatus?.expiresAt && (
          <div className="space-y-1">
            <div className="text-xs text-gray-400">
              Valid for <span className="text-gray-200 font-medium">{fmtExpiry(codexStatus.expiresAt)}</span>
            </div>
            {codexStatus.authMode && (
              <div className="text-xs text-gray-500">Mode: {codexStatus.authMode}</div>
            )}
            <button
              onClick={async () => {
                if (!confirm("Disconnect Codex CLI?")) return;
                await settingsApi.codexDisconnect();
                setCodexStatus({ hasCredentials: false, valid: false, expiresAt: null, authMode: null });
              }}
              className="flex items-center gap-1 px-2.5 py-1.5 text-xs text-gray-500 hover:text-red-400 hover:bg-gray-800 border border-gray-700 rounded-lg transition-colors"
            >
              <Unlink className="w-3 h-3" /> Disconnect
            </button>
          </div>
        )}

        {connecting && loginStatus?.status === "waiting_for_auth" && (
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-xs text-emerald-400">
              <Loader2 className="w-3 h-3 animate-spin" />
              {loginStatus.message || "Authenticating..."}
            </div>
            {loginStatus.authUrl && (
              <a
                href={loginStatus.authUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-3 py-2 bg-emerald-600/10 border border-emerald-500/30 rounded-lg text-xs text-emerald-400 hover:bg-emerald-600/20 transition-colors"
              >
                <ExternalLink className="w-3 h-3" /> Open Authorization Page
              </a>
            )}
            {loginStatus.authCode && (
              <div className="px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-center">
                <p className="text-xs text-gray-500 mb-0.5">Code:</p>
                <p className="text-lg font-mono font-bold text-gray-100 tracking-widest select-all">{loginStatus.authCode}</p>
              </div>
            )}
            <button
              onClick={handleCancel}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-gray-800 hover:bg-gray-700 border border-gray-700 text-gray-400 rounded-lg transition-colors"
            >
              <X className="w-3 h-3" /> Cancel
            </button>
          </div>
        )}

        {loginStatus?.status === "error" && !connecting && (
          <div className="text-xs text-red-400">{loginStatus.error || "Login failed"}</div>
        )}

        {!isConnected && !connecting && loginStatus?.status !== "error" && cliStatus?.installed && (
          <button
            onClick={handleConnect}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium rounded-lg transition-colors"
          >
            <Link2 className="w-3 h-3" /> Connect Codex
          </button>
        )}
      </div>
    </div>
  );
}

// ─── GA Connection Card ─────────────────────────────────

function GAConnectionCard() {
  const [status, setStatus] = useState<GAConnectionStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [showAccountPicker, setShowAccountPicker] = useState(false);
  const [accounts, setAccounts] = useState<GAAccount[]>([]);
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [savingAccount, setSavingAccount] = useState(false);

  const loadStatus = useCallback(async () => {
    try {
      const s = await analytics.status();
      setStatus(s);
      return s;
    } catch {
      setStatus({ connected: false });
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  useEffect(() => {
    function handler(e: MessageEvent) {
      if (e.data?.type === "ga-connected") {
        loadStatus().then((s) => {
          if (s?.connected && !s.accountId) openAccountPicker();
        });
      }
    }
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, [loadStatus]);

  async function handleConnect() {
    setConnecting(true);
    try {
      const { authUrl } = await analytics.connect();
      window.open(authUrl, "ga-auth", "width=600,height=700");
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : "Failed to start connection");
    } finally {
      setConnecting(false);
    }
  }

  async function handleDisconnect() {
    if (!confirm("Disconnect Google Analytics?")) return;
    await analytics.disconnect();
    setStatus({ connected: false });
  }

  async function openAccountPicker() {
    setShowAccountPicker(true);
    setAccountsLoading(true);
    try {
      const res = await analytics.accounts();
      setAccounts(res.data);
      if (res.data.length === 1) await selectAccount(res.data[0]!);
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : "Failed to load accounts");
      setShowAccountPicker(false);
    } finally {
      setAccountsLoading(false);
    }
  }

  async function selectAccount(account: GAAccount) {
    setSavingAccount(true);
    try {
      const res = await analytics.setAccount(account.name, account.displayName, account.properties);
      setStatus((prev) => prev ? { ...prev, accountId: account.name, accountName: account.displayName } : prev);
      setShowAccountPicker(false);
      if (res.linked && res.linked.length > 0) {
        alert(`Auto-linked ${res.linked.length} site(s):\n${res.linked.map((l) => `  ${l.domain} -> ${l.propertyId}`).join("\n")}`);
      }
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : "Failed to set account");
    } finally {
      setSavingAccount(false);
    }
  }

  if (loading) return null;

  return (
    <>
      <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
        <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-800">
          <div className="w-8 h-8 bg-blue-500/10 rounded-lg flex items-center justify-center">
            <BarChart3 className="w-4 h-4 text-blue-400" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-semibold text-gray-100">GA Connection</p>
            <p className="text-xs text-gray-500">OAuth link to your GA4 account</p>
          </div>
          {status?.connected && (
            <span className="flex items-center gap-1 text-xs text-green-400 bg-green-500/10 px-2 py-0.5 rounded-full">
              <CheckCircle2 className="w-3 h-3" /> Connected
            </span>
          )}
        </div>

        <div className="p-4">
          {!status?.connected ? (
            <div className="text-center py-2">
              <p className="text-xs text-gray-500 mb-3">
                {(status as { hasCredentials?: boolean } | null)?.hasCredentials === false
                  ? "Add GA credentials above first"
                  : "Connect your Google Analytics account"}
              </p>
              <button onClick={handleConnect} disabled={connecting}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs bg-brand-600 hover:bg-brand-700 disabled:opacity-50 text-white rounded-lg transition-colors">
                <Link2 className="w-3 h-3" />
                {connecting ? "Connecting..." : "Connect GA"}
              </button>
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <div className="text-xs">
                {status.accountId ? (
                  <span className="text-gray-200">{status.accountName ?? status.accountId}</span>
                ) : (
                  <span className="text-amber-400">No account selected</span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button onClick={openAccountPicker}
                  className="flex items-center gap-1 px-2.5 py-1 text-xs bg-gray-800 hover:bg-gray-700 border border-gray-700 text-gray-300 rounded-lg transition-colors">
                  <ChevronRight className="w-3 h-3" />
                  {status.accountId ? "Change" : "Choose"}
                </button>
                <button onClick={handleDisconnect}
                  className="flex items-center gap-1 px-2.5 py-1 text-xs text-gray-500 hover:text-red-400 hover:bg-gray-800 border border-gray-700 rounded-lg transition-colors">
                  <Unlink className="w-3 h-3" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {showAccountPicker && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" onClick={() => setShowAccountPicker(false)}>
          <div className="bg-gray-900 border border-gray-700 rounded-xl w-full max-w-md p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-gray-100 mb-4">Choose Analytics Account</h3>
            {accountsLoading ? (
              <div className="flex items-center justify-center h-32">
                <div className="w-6 h-6 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : accounts.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-6">No GA4 accounts found.</p>
            ) : (
              <div className="space-y-2 max-h-72 overflow-y-auto">
                {accounts.map((acct) => (
                  <button key={acct.name} onClick={() => selectAccount(acct)} disabled={savingAccount}
                    className={`w-full flex items-center justify-between px-4 py-3 border rounded-lg hover:border-brand-500/50 transition-colors text-left disabled:opacity-50 ${
                      status?.accountId === acct.name ? "bg-brand-600/10 border-brand-500/50" : "bg-gray-800 border-gray-700"
                    }`}>
                    <div>
                      <p className="text-sm font-medium text-gray-100">{acct.displayName}</p>
                      <p className="text-xs text-gray-500">{acct.properties.length} properties</p>
                    </div>
                    {status?.accountId === acct.name && <CheckCircle2 className="w-4 h-4 text-brand-400" />}
                  </button>
                ))}
              </div>
            )}
            <button onClick={() => setShowAccountPicker(false)}
              className="mt-4 w-full py-2 text-sm text-gray-400 hover:text-gray-200 bg-gray-800 rounded-lg transition-colors">
              Cancel
            </button>
          </div>
        </div>
      )}
    </>
  );
}

// ─── Gemini Connection Card ──────────────────────────────

function GeminiConnectionCard() {
  const [cliStatus, setCliStatus] = useState<{ installed: boolean; version?: string } | null>(null);
  const [authenticated, setAuthenticated] = useState<boolean | null>(null);
  const [connecting, setConnecting] = useState(false);
  const [loginStatus, setLoginStatus] = useState<GeminiLoginStatus | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    settingsApi.geminiCheck().then(setCliStatus).catch(() => setCliStatus({ installed: false }));
    settingsApi.geminiStatus().then((r) => setAuthenticated(r.authenticated)).catch(() => setAuthenticated(false));
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  function startPolling() {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const status = await settingsApi.geminiLoginStatus();
        setLoginStatus(status);
        if (status.status === "success" || status.status === "error") {
          if (pollRef.current) clearInterval(pollRef.current);
          pollRef.current = null;
          setConnecting(false);
          if (status.status === "success") setAuthenticated(true);
        }
      } catch {}
    }, 2000);
  }

  async function handleConnect() {
    setConnecting(true);
    setLoginStatus(null);
    try {
      const res = await settingsApi.geminiLogin();
      setLoginStatus(res);
      startPolling();
    } catch {
      setConnecting(false);
      setLoginStatus({ status: "error", error: "Failed to start login" });
    }
  }

  async function handleCancel() {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = null;
    setConnecting(false);
    setLoginStatus(null);
    await settingsApi.geminiLoginCancel().catch(() => {});
  }

  const isConnected = authenticated === true;

  return (
    <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
      <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-800">
        <div className="w-8 h-8 bg-cyan-500/10 rounded-lg flex items-center justify-center">
          <Cpu className="w-4 h-4 text-cyan-400" />
        </div>
        <div className="flex-1">
          <p className="text-sm font-semibold text-gray-100">Gemini CLI</p>
          <p className="text-xs text-gray-500">OAuth connection for Gemini AI</p>
        </div>
        {isConnected && (
          <span className="flex items-center gap-1 text-xs text-green-400 bg-green-500/10 px-2 py-0.5 rounded-full">
            <CheckCircle2 className="w-3 h-3" /> Connected
          </span>
        )}
      </div>

      <div className="p-4 space-y-3">
        {cliStatus === null ? (
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <Loader2 className="w-3 h-3 animate-spin" /> Checking CLI...
          </div>
        ) : !cliStatus.installed ? (
          <div className="text-xs text-gray-500">Gemini CLI not found on the server.</div>
        ) : null}

        {isConnected && !connecting && (
          <div className="flex items-center justify-between">
            <div className="text-xs text-gray-400">Gemini CLI authenticated</div>
          </div>
        )}

        {connecting && loginStatus?.status === "waiting_for_auth" && (
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-xs text-cyan-400">
              <Loader2 className="w-3 h-3 animate-spin" />
              {loginStatus.message || "Authenticating..."}
            </div>
            {loginStatus.authUrl && (
              <a href={loginStatus.authUrl} target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-2 px-3 py-2 bg-cyan-600/10 border border-cyan-500/30 rounded-lg text-xs text-cyan-400 hover:bg-cyan-600/20 transition-colors">
                <ExternalLink className="w-3 h-3" /> Open Authorization Page
              </a>
            )}
            <button onClick={handleCancel}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-gray-800 hover:bg-gray-700 border border-gray-700 text-gray-400 rounded-lg transition-colors">
              <X className="w-3 h-3" /> Cancel
            </button>
          </div>
        )}

        {loginStatus?.status === "error" && !connecting && (
          <div className="text-xs text-red-400">{loginStatus.error || "Login failed"}</div>
        )}

        {!isConnected && !connecting && loginStatus?.status !== "error" && cliStatus?.installed && (
          <button onClick={handleConnect}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white text-xs font-medium rounded-lg transition-colors">
            <Link className="w-3 h-3" /> Connect Gemini
          </button>
        )}
      </div>
    </div>
  );
}

// ─── MCP Server Cards ───────────────────────────────────

function McpUrlCard({ title, description, url, icon: Icon, iconColor }: {
  title: string;
  description: string;
  url: string;
  icon: React.ElementType;
  iconColor: string;
}) {
  return (
    <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
      <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-800">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${iconColor}`}>
          <Icon className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-gray-100">{title}</p>
          <p className="text-xs text-gray-500 truncate">{description}</p>
        </div>
        <span className="flex items-center gap-1 text-xs text-green-400 bg-green-500/10 px-2 py-0.5 rounded-full flex-shrink-0">
          <CheckCircle2 className="w-3 h-3" /> Active
        </span>
      </div>
      <div className="p-4">
        <code className="text-xs font-mono text-gray-400 bg-gray-800 px-2 py-1 rounded block truncate">
          {url}
        </code>
      </div>
    </div>
  );
}

// ─── Team Members Section ────────────────────────────────

const ROLE_COLORS: Record<TenantUserRole, string> = {
  owner: "bg-amber-500/10 text-amber-400",
  admin: "bg-purple-500/10 text-purple-400",
  editor: "bg-blue-500/10 text-blue-400",
  viewer: "bg-gray-500/10 text-gray-400",
};

function TeamSection() {
  const [members, setMembers] = useState<TenantUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  // Invite form
  const [inviteName, setInviteName] = useState("");
  const [inviteEmail, setInviteEmail] = useState("");
  const [invitePassword, setInvitePassword] = useState("");
  const [inviteRole, setInviteRole] = useState<TenantUserRole>("editor");
  const [inviteSaving, setInviteSaving] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);

  // Edit form
  const [editName, setEditName] = useState("");
  const [editEmail, setEditEmail] = useState("");
  const [editRole, setEditRole] = useState<TenantUserRole>("editor");
  const [editPassword, setEditPassword] = useState("");
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  useEffect(() => {
    loadMembers();
  }, []);

  async function loadMembers() {
    try {
      const res = await usersApi.list();
      setMembers(res.data);
    } catch {} finally {
      setLoading(false);
    }
  }

  async function handleInvite(e: FormEvent) {
    e.preventDefault();
    setInviteError(null);
    setInviteSaving(true);
    try {
      await usersApi.create({ name: inviteName, email: inviteEmail, password: invitePassword, role: inviteRole });
      setShowInvite(false);
      setInviteName("");
      setInviteEmail("");
      setInvitePassword("");
      setInviteRole("editor");
      await loadMembers();
    } catch (err: unknown) {
      setInviteError(err instanceof Error ? err.message : "Failed to create user");
    } finally {
      setInviteSaving(false);
    }
  }

  function startEdit(user: TenantUser) {
    setEditingId(user._id);
    setEditName(user.name);
    setEditEmail(user.email);
    setEditRole(user.role);
    setEditPassword("");
    setEditError(null);
  }

  async function handleEdit(e: FormEvent) {
    e.preventDefault();
    if (!editingId) return;
    setEditError(null);
    setEditSaving(true);
    try {
      const patch: Record<string, string> = { name: editName, email: editEmail, role: editRole };
      if (editPassword) patch["password"] = editPassword;
      await usersApi.update(editingId, patch as Parameters<typeof usersApi.update>[1]);
      setEditingId(null);
      await loadMembers();
    } catch (err: unknown) {
      setEditError(err instanceof Error ? err.message : "Failed to update user");
    } finally {
      setEditSaving(false);
    }
  }

  async function handleDelete(user: TenantUser) {
    if (!confirm(`Remove ${user.name} from the team?`)) return;
    try {
      await usersApi.delete(user._id);
      await loadMembers();
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : "Failed to remove user");
    }
  }

  const inputCls = "w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-gray-100 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-brand-500";
  const labelCls = "block text-xs font-medium text-gray-400 mb-1";

  if (loading) {
    return (
      <div className="flex items-center justify-center h-24">
        <Loader2 className="w-5 h-5 text-brand-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-medium text-gray-400">Team Members</h2>
        <button
          onClick={() => setShowInvite(!showInvite)}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-brand-600 hover:bg-brand-700 text-white rounded-lg transition-colors"
        >
          <Plus className="w-3 h-3" />
          Add Member
        </button>
      </div>

      {/* Invite form */}
      {showInvite && (
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
          <h3 className="text-sm font-semibold text-gray-100 mb-4">Add Team Member</h3>
          {inviteError && (
            <div className="mb-3 px-3 py-2 bg-red-500/10 border border-red-500/20 rounded-lg text-xs text-red-400">
              {inviteError}
            </div>
          )}
          <form onSubmit={handleInvite} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Name</label>
                <input value={inviteName} onChange={(e) => setInviteName(e.target.value)} required placeholder="Jane Doe" className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Email</label>
                <input type="email" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} required placeholder="jane@example.com" className={inputCls} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Password</label>
                <input type="password" value={invitePassword} onChange={(e) => setInvitePassword(e.target.value)} required minLength={6} placeholder="Min 6 characters" className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Role</label>
                <select value={inviteRole} onChange={(e) => setInviteRole(e.target.value as TenantUserRole)} className={inputCls}>
                  <option value="admin">Admin</option>
                  <option value="editor">Editor</option>
                  <option value="viewer">Viewer</option>
                </select>
              </div>
            </div>
            <div className="flex gap-2 pt-1">
              <button type="submit" disabled={inviteSaving} className="flex items-center gap-1.5 px-4 py-2 text-xs bg-brand-600 hover:bg-brand-700 disabled:opacity-50 text-white rounded-lg transition-colors">
                {inviteSaving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
                {inviteSaving ? "Creating..." : "Create"}
              </button>
              <button type="button" onClick={() => setShowInvite(false)} className="px-4 py-2 text-xs text-gray-400 hover:text-gray-200 bg-gray-800 rounded-lg transition-colors">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Members list */}
      {members.length === 0 ? (
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-8 text-center">
          <Users className="w-8 h-8 text-gray-600 mx-auto mb-2" />
          <p className="text-sm text-gray-500">No team members yet</p>
          <p className="text-xs text-gray-600 mt-1">Add members to give them access to the dashboard</p>
        </div>
      ) : (
        <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden divide-y divide-gray-800">
          {members.map((user) => (
            <div key={user._id}>
              {editingId === user._id ? (
                <div className="p-4">
                  {editError && (
                    <div className="mb-3 px-3 py-2 bg-red-500/10 border border-red-500/20 rounded-lg text-xs text-red-400">
                      {editError}
                    </div>
                  )}
                  <form onSubmit={handleEdit} className="space-y-3">
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <label className={labelCls}>Name</label>
                        <input value={editName} onChange={(e) => setEditName(e.target.value)} required className={inputCls} />
                      </div>
                      <div>
                        <label className={labelCls}>Email</label>
                        <input type="email" value={editEmail} onChange={(e) => setEditEmail(e.target.value)} required className={inputCls} />
                      </div>
                      <div>
                        <label className={labelCls}>Role</label>
                        <select
                          value={editRole}
                          onChange={(e) => setEditRole(e.target.value as TenantUserRole)}
                          disabled={user.role === "owner"}
                          className={inputCls}
                        >
                          {user.role === "owner" && <option value="owner">Owner</option>}
                          <option value="admin">Admin</option>
                          <option value="editor">Editor</option>
                          <option value="viewer">Viewer</option>
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className={labelCls}>New Password (leave blank to keep)</label>
                      <input type="password" value={editPassword} onChange={(e) => setEditPassword(e.target.value)} minLength={6} placeholder="Leave blank to keep current" className={inputCls} />
                    </div>
                    <div className="flex gap-2">
                      <button type="submit" disabled={editSaving} className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-brand-600 hover:bg-brand-700 disabled:opacity-50 text-white rounded-lg transition-colors">
                        {editSaving ? "Saving..." : "Save"}
                      </button>
                      <button type="button" onClick={() => setEditingId(null)} className="px-3 py-1.5 text-xs text-gray-400 hover:text-gray-200 bg-gray-800 rounded-lg transition-colors">
                        Cancel
                      </button>
                    </div>
                  </form>
                </div>
              ) : (
                <div className="flex items-center gap-3 px-4 py-3 group">
                  <div className="w-8 h-8 rounded-full bg-gray-800 flex items-center justify-center text-gray-300 text-sm font-semibold flex-shrink-0">
                    {user.name[0]?.toUpperCase() ?? "?"}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-200 truncate">{user.name}</p>
                    <p className="text-xs text-gray-500 truncate">{user.email}</p>
                  </div>
                  <span className={`text-[10px] font-medium uppercase tracking-wider px-2 py-0.5 rounded-full flex-shrink-0 ${ROLE_COLORS[user.role]}`}>
                    {user.role}
                  </span>
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                    <button
                      onClick={() => startEdit(user)}
                      className="p-1.5 text-gray-500 hover:text-gray-300 hover:bg-gray-800 rounded transition-colors"
                      title="Edit"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    {user.role !== "owner" && (
                      <button
                        onClick={() => handleDelete(user)}
                        className="p-1.5 text-gray-500 hover:text-red-400 hover:bg-gray-800 rounded transition-colors"
                        title="Remove"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Main Settings Page ──────────────────────────────────

type SettingsTab = "integrations" | "users" | "profile";

const SETTINGS_TABS: { key: SettingsTab; label: string }[] = [
  { key: "integrations", label: "Integrations" },
  { key: "users", label: "Users" },
  { key: "profile", label: "Profile" },
];

export default function Settings() {
  const { tenant } = useAuth();
  const { theme, toggleTheme } = useTheme();

  const [activeTab, setActiveTab] = useState<SettingsTab>("integrations");
  const [savedCreds, setSavedCreds] = useState<Record<string, Record<string, string | boolean>>>({});
  const [loadingCreds, setLoadingCreds] = useState(true);

  const [accountForm, setAccountForm] = useState({
    name: tenant?.name ?? "",
    email: tenant?.email ?? "",
  });
  const [savingAccount, setSavingAccount] = useState(false);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  useEffect(() => {
    settingsApi.getCredentials()
      .then((res) => setSavedCreds(res.data))
      .catch(() => {})
      .finally(() => setLoadingCreds(false));
  }, []);

  function showSuccess(msg: string) {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(null), 3000);
  }

  async function handleSaveCreds(provider: string, values: Record<string, string>) {
    await settingsApi.saveCredentials(provider, values);
    const res = await settingsApi.getCredentials();
    setSavedCreds(res.data);
    showSuccess(`${provider} credentials saved`);
  }

  async function handleDeleteCreds(provider: string) {
    await settingsApi.deleteCredentials(provider);
    const res = await settingsApi.getCredentials();
    setSavedCreds(res.data);
    showSuccess(`${provider} credentials removed`);
  }

  async function saveAccount(e: FormEvent) {
    e.preventDefault();
    setSavingAccount(true);
    await new Promise((r) => setTimeout(r, 800));
    setSavingAccount(false);
    showSuccess("Account updated");
  }

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      {/* Header + Tabs */}
      <div>
        <h1 className="text-xl font-bold text-gray-100">Settings</h1>
        <div className="flex gap-1 mt-4 border-b border-gray-800">
          {SETTINGS_TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-4 py-2 text-sm font-medium transition-colors relative ${
                activeTab === tab.key
                  ? "text-brand-400"
                  : "text-gray-500 hover:text-gray-300"
              }`}
            >
              {tab.label}
              {activeTab === tab.key && (
                <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-brand-500 rounded-full" />
              )}
            </button>
          ))}
        </div>
      </div>

      {successMsg && (
        <div className="flex items-center gap-2 px-4 py-3 bg-green-500/10 border border-green-500/20 rounded-lg text-sm text-green-400">
          <CheckCircle2 className="w-4 h-4" />
          {successMsg}
        </div>
      )}

      {/* ═══════════ INTEGRATIONS TAB ═══════════ */}
      {activeTab === "integrations" && (
        <div className="space-y-8">
          {/* API Credentials Grid */}
          <div>
            <h2 className="text-sm font-medium text-gray-400 mb-3">API Credentials</h2>
            {loadingCreds ? (
              <div className="flex items-center justify-center h-32">
                <Loader2 className="w-5 h-5 text-brand-500 animate-spin" />
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {CREDENTIAL_CARDS.map((config) => (
                  <CredentialCard
                    key={config.provider}
                    config={config}
                    savedData={savedCreds[config.provider]}
                    onSave={handleSaveCreds}
                    onDelete={handleDeleteCreds}
                  />
                ))}
              </div>
            )}
          </div>

          {/* OAuth Connections */}
          <div>
            <h2 className="text-sm font-medium text-gray-400 mb-3">OAuth Connections</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <CodexConnectionCard />
              <ClaudeConnectionCard />
              <GAConnectionCard />
              <GeminiConnectionCard />
            </div>
          </div>

          {/* MCP Servers */}
          <div>
            <h2 className="text-sm font-medium text-gray-400 mb-3">MCP Servers</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <McpUrlCard
                title="DataForSEO MCP"
                description="Keyword research and SERP analysis"
                url="dataforseo.mcp.ottolax.com"
                icon={Search}
                iconColor="bg-green-500/10 text-green-400"
              />
              <McpUrlCard
                title="eBay Partner MCP"
                description="eBay product search and affiliate links"
                url="epn.mcp.ottolax.com"
                icon={ShoppingCart}
                iconColor="bg-red-500/10 text-red-400"
              />
              <McpUrlCard
                title="Google Analytics MCP"
                description="Analytics data and property management"
                url="google-analytics.mcp.ottolax.com"
                icon={BarChart3}
                iconColor="bg-blue-500/10 text-blue-400"
              />
              <McpUrlCard
                title="Cloudflare MCP"
                description="DNS and Cloudflare Pages management"
                url="cloudflare.mcp.ottolax.com"
                icon={Cloud}
                iconColor="bg-amber-500/10 text-amber-400"
              />
              <McpUrlCard
                title="GoDaddy MCP"
                description="Domain registration and DNS management"
                url="godaddy.mcp.ottolax.com"
                icon={Database}
                iconColor="bg-teal-500/10 text-teal-400"
              />
            </div>
          </div>
        </div>
      )}

      {/* ═══════════ USERS TAB ═══════════ */}
      {activeTab === "users" && (
        <TeamSection />
      )}

      {/* ═══════════ PROFILE TAB ═══════════ */}
      {activeTab === "profile" && (
        <div className="space-y-8">
          {/* Account */}
          <div>
            <h2 className="text-sm font-medium text-gray-400 mb-3">Account</h2>
            <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
              <form onSubmit={saveAccount} className="p-5 space-y-4">
                <div className="flex items-center gap-4 mb-2">
                  <div className="w-10 h-10 rounded-full bg-brand-600 flex items-center justify-center text-white font-semibold">
                    {(tenant?.name?.[0] ?? "?").toUpperCase()}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-200">{tenant?.name}</p>
                    <p className="text-xs text-gray-500">{tenant?.plan ?? "free"} plan</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-400 mb-1.5">Name</label>
                    <input
                      value={accountForm.name}
                      onChange={(e) => setAccountForm((f) => ({ ...f, name: e.target.value }))}
                      className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-gray-100 focus:outline-none focus:ring-2 focus:ring-brand-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-400 mb-1.5">Email</label>
                    <input type="email"
                      value={accountForm.email}
                      onChange={(e) => setAccountForm((f) => ({ ...f, email: e.target.value }))}
                      className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-gray-100 focus:outline-none focus:ring-2 focus:ring-brand-500"
                    />
                  </div>
                </div>

                <div className="flex justify-end">
                  <button type="submit" disabled={savingAccount}
                    className="flex items-center gap-2 px-4 py-2 bg-brand-600 hover:bg-brand-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors">
                    <Save className="w-4 h-4" />
                    {savingAccount ? "Saving..." : "Save"}
                  </button>
                </div>
              </form>
            </div>
          </div>

          {/* Appearance */}
          <div>
            <h2 className="text-sm font-medium text-gray-400 mb-3">Appearance</h2>
            <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
              <div className="flex items-center justify-between px-5 py-4">
                <div>
                  <p className="text-sm font-semibold text-gray-100">Theme</p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {theme === "dark" ? "Dark mode" : "Light mode"}
                  </p>
                </div>
                <button onClick={toggleTheme}
                  className={`relative w-11 h-6 rounded-full transition-colors ${theme === "dark" ? "bg-brand-600" : "bg-gray-600"}`}>
                  <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                    theme === "dark" ? "translate-x-5" : "translate-x-0"
                  }`} />
                </button>
              </div>
            </div>
          </div>

          {/* Danger zone */}
          <div className="bg-gray-900 border border-red-900/30 rounded-xl p-5">
            <p className="text-sm font-semibold text-red-400 mb-1">Danger Zone</p>
            <p className="text-xs text-gray-500 mb-4">These actions are irreversible.</p>
            <button className="px-4 py-2 bg-red-900/20 hover:bg-red-900/40 border border-red-900/40 text-red-400 text-sm rounded-lg transition-colors">
              Delete Account
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
